import { createSelector } from "@reduxjs/toolkit";
import { RBAC } from "../constants/rbacPermissionKeys";
import type {
  CaseDetailRbacPermissions,
  CaseListActionPermissions,
} from "../types/caseRbacPermissions";
import type { RootState } from "../types/store";

const RBAC_VALUE_KEYS: readonly string[] = Object.values(RBAC);

export interface PermissionApiPayload {
  permissions?: Record<string, boolean>;
  role_permissions?: Array<{
    action_shortcode?: string;
    has_permission?: boolean;
  }>;
  role_info?: { role_shortcode?: string; role_name?: string };
}

function normalizePermissionKey(key: string): string {
  return key.trim().replace(/-/g, "_");
}

function coercePermissionBoolean(value: unknown): boolean | undefined {
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    if (s === "true" || s === "1") return true;
    if (s === "false" || s === "0") return false;
  }
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return coercePermissionBoolean((value as { has_permission?: unknown }).has_permission);
  }
  return undefined;
}

function normalizePermissionsRecord(raw: Record<string, unknown>): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const [key, value] of Object.entries(raw)) {
    const b = coercePermissionBoolean(value);
    if (b !== undefined) out[normalizePermissionKey(key)] = b;
  }
  return out;
}

function readPayloadFlag(
  payload: Record<string, unknown>,
  underscoreKey: string
): boolean | undefined {
  if (underscoreKey in payload) {
    return coercePermissionBoolean(payload[underscoreKey]);
  }
  const hyphenKey = underscoreKey.replace(/_/g, "-");
  if (hyphenKey in payload) {
    return coercePermissionBoolean(payload[hyphenKey]);
  }
  return undefined;
}

export function mergePermissionFlagsFromPayload(
  payload: PermissionApiPayload | Record<string, unknown> | null | undefined
): Record<string, boolean> {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return {};
  }
  const record = payload as Record<string, unknown>;
  const top: Record<string, boolean> = {};
  for (let i = 0; i < RBAC_VALUE_KEYS.length; i += 1) {
    const key = RBAC_VALUE_KEYS[i];
    const b = readPayloadFlag(record, key);
    if (b !== undefined) top[key] = b;
  }
  const nested = (payload as PermissionApiPayload).permissions;
  const fromNested =
    nested && typeof nested === "object" && !Array.isArray(nested)
      ? normalizePermissionsRecord(nested as Record<string, unknown>)
      : {};
  const fromRolePermissions: Record<string, boolean> = {};
  const rolePermissions = (payload as PermissionApiPayload).role_permissions;
  if (Array.isArray(rolePermissions)) {
    for (const entry of rolePermissions) {
      const shortcode = entry?.action_shortcode;
      if (!shortcode) continue;
      const b = coercePermissionBoolean(entry.has_permission);
      if (b !== undefined) fromRolePermissions[normalizePermissionKey(String(shortcode))] = b;
    }
  }
  return { ...top, ...fromRolePermissions, ...fromNested };
}

function payloadHasTopLevelRbacFlags(p: object): boolean {
  const record = p as Record<string, unknown>;
  for (let i = 0; i < RBAC_VALUE_KEYS.length; i += 1) {
    const key = RBAC_VALUE_KEYS[i];
    if (readPayloadFlag(record, key) !== undefined) {
      return true;
    }
  }
  return false;
}

export function selectPermissionApiPayload(state: RootState): PermissionApiPayload | undefined {
  const raw = state.permission?.permissions;
  if (!raw || typeof raw !== "object") return undefined;
  return raw as PermissionApiPayload;
}

function permissionPayloadHasData(p: PermissionApiPayload | null | undefined): boolean {
  if (!p || typeof p !== "object") return false;
  const nested = p.permissions;
  if (nested && typeof nested === "object" && Object.keys(nested).length > 0) return true;
  if (Array.isArray(p.role_permissions) && p.role_permissions.length > 0) return true;
  if (payloadHasTopLevelRbacFlags(p)) return true;
  if (p.role_info?.role_shortcode || p.role_info?.role_name) return true;
  return false;
}

export function selectEffectivePermissionPayload(state: RootState): PermissionApiPayload | null {
  const fromSlice = selectPermissionApiPayload(state);
  if (permissionPayloadHasData(fromSlice)) {
    return fromSlice ?? null;
  }
  const fromLogin = state.loginDetails?.logedInUsersDetails?.user_permissions as
    | PermissionApiPayload
    | undefined;
  if (permissionPayloadHasData(fromLogin)) {
    return fromLogin ?? null;
  }
  return null;
}

export function permissionFlagEnabled(
  payload: { permissions?: Record<string, boolean> } | null | undefined,
  featureKey: string
): boolean {
  const flags = mergePermissionFlagsFromPayload(payload as PermissionApiPayload | null);
  const key = normalizePermissionKey(featureKey);
  return key in flags && flags[key] === true;
}

export function selectPermissionFeature(state: RootState, featureKey: string): boolean {
  return permissionFlagEnabled(selectEffectivePermissionPayload(state), featureKey);
}

export function selectCanCreateRoles(state: RootState): boolean {
  return selectPermissionFeature(state, "create_roles");
}

export function selectCanCreateTags(state: RootState): boolean {
  return selectPermissionFeature(state, "create_tags");
}

export function selectCanCreateClient(state: RootState): boolean {
  return selectPermissionFeature(state, "create_client");
}

export function selectCanCreateCase(state: RootState): boolean {
  return selectPermissionFeature(state, "create_case");
}

export function selectCanCreateUsers(state: RootState): boolean {
  return selectPermissionFeature(state, "create_users");
}

export function selectUserRoleShortcodeFromPermissions(state: RootState): string | null {
  return selectEffectivePermissionPayload(state)?.role_info?.role_shortcode ?? null;
}

export function selectUserRoleNameFromPermissions(state: RootState): string | null {
  const name = selectEffectivePermissionPayload(state)?.role_info?.role_name;
  if (name === undefined || name === null) return null;
  const s = String(name).trim();
  return s !== "" ? s : null;
}

const EMPTY_FLAGS: Record<string, boolean> = Object.freeze({});

function readFlag(flags: Record<string, boolean>, key: string): boolean {
  const normalized = normalizePermissionKey(key);
  return normalized in flags && flags[normalized] === true;
}

function readFlagWithFallback(
  flags: Record<string, boolean>,
  primary: string,
  fallback: string
): boolean {
  const primaryKey = normalizePermissionKey(primary);
  if (primaryKey in flags) return flags[primaryKey] === true;
  return readFlag(flags, fallback);
}

function mapCaseListActionPermissions(flags: Record<string, boolean>): CaseListActionPermissions {
  return {
    viewCaseDetails: readFlag(flags, RBAC.view_case_details),
    viewClients: readFlag(flags, RBAC.view_clients),
    viewClientDetails: readFlag(flags, RBAC.view_client_details),
    createCase: readFlag(flags, RBAC.create_case),
    confirmCase: readFlag(flags, RBAC.confirm_case),
    switchStep: readFlag(flags, RBAC.switch_step),
    closeCase: readFlag(flags, RBAC.close_case),
    manageCaseWorkflow: readFlagWithFallback(flags, RBAC.manage_case_workflow, RBAC.switch_step),
    canDeleteCase: readFlagWithFallback(flags, RBAC.delete_case, RBAC.create_case),
    answerQuestions: readFlag(flags, RBAC.answer_questions),
    addDocuments: readFlag(flags, RBAC.add_documents),
  };
}

function mapCaseDetailRbacPermissions(flags: Record<string, boolean>): CaseDetailRbacPermissions {
  return {
    ...mapCaseListActionPermissions(flags),
    addNotes: readFlag(flags, RBAC.add_notes),
    sendMessages: readFlag(flags, RBAC.send_messages),
    assignUserToCase: readFlag(flags, RBAC.assign_user_to_case),
    assignTagsToCase: readFlag(flags, RBAC.assign_tags_to_case),
    assignUserToStep: readFlag(flags, RBAC.assign_user_to_step),
    switchProcess: readFlag(flags, RBAC.switch_process),
    createTags: readFlag(flags, RBAC.create_tags),
    createGroups: readFlag(flags, RBAC.create_groups),
  };
}

export const selectEffectivePermissionFlags = createSelector(
  selectEffectivePermissionPayload,
  (payload): Record<string, boolean> => {
    const merged = mergePermissionFlagsFromPayload(payload);
    return Object.keys(merged).length > 0 ? merged : EMPTY_FLAGS;
  }
);

export const selectCaseListActionPermissions = createSelector(
  selectEffectivePermissionFlags,
  mapCaseListActionPermissions
);

export const selectCaseDetailRbacPermissions = createSelector(
  selectEffectivePermissionFlags,
  mapCaseDetailRbacPermissions
);
